'use client';

import React from 'react';
import { motion, Variants, Transition } from 'framer-motion';
import dynamic from 'next/dynamic';

const DynamicSpiderChart = dynamic(() => import('../../../benchmark/components/SpiderChart'), {
  ssr: false,
  loading: () => <p className="text-center text-gray-500">Loading Chart...</p>,
});

interface WelcomeStepProps {
  onStartAssessment: () => void;
  pageVariants?: Variants;
  pageTransition?: Transition;
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({
  onStartAssessment,
  pageVariants,
  pageTransition,
}) => {
  const variants = pageVariants || {};
  const transition = pageTransition || {};

  return (
    <motion.div
      key="welcome"
      initial="initial"
      animate="in"
      exit="out"
      variants={variants}
      transition={transition}
      className="flex flex-col items-center justify-center text-center min-h-[70vh]"
    >
      <div className="w-full max-w-8xl flex flex-col lg:flex-row lg:items-start lg:gap-8 px-4">
        <div className="flex-1 lg:w-[40%] mb-8 lg:mb-0 flex flex-col justify-center text-center lg:text-left">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-3xl md:text-4xl font-bold text-white mb-4"
          >
            Rate Yourself
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-2xl text-gray-300 mb-4"
          >
            How well are you personalising your engagement with healthcare professionals?
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-base text-gray-400 mb-6"
          >
            Take the assessment to discover your personalisation score across 7 key dimensions of
            HCP engagement strategy.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="text-base text-gray-400 mb-2"
          >
            At the end, you see your score against:
          </motion.p>
          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="list-disc ml-6 text-base text-gray-400 mb-6 text-left"
          >
            <li>Best practice pharma leaders</li>
            <li>Industry averages</li>
          </motion.ul>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center md:text-left"
          >
            <motion.button
              type="button"
              onClick={onStartAssessment}
              className="mt-2 rounded-lg bg-[#59B9C1]/75 px-6 py-3 font-semibold text-white transition-all duration-200 ease-out"
              whileHover={{ scale: 1.05, backgroundColor: '#59B9C1' }}
              whileTap={{ scale: 0.95 }}
            >
              Start Assessment
            </motion.button>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-2 text-sm text-gray-500"
            >
              (Takes approx. 5 minutes)
            </motion.p>
          </motion.div>
        </div>

        <div className="flex-1 lg:w-[60%] flex flex-col gap-6 items-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="w-full"
          >
            <div className="bg-gray-800/80 rounded-xl p-6 shadow-md text-left border border-gray-700">
              <h4 className="text-lg font-semibold text-[#59B9C1] mb-4">
                HCP Personalisation done right:
              </h4>
              <ul className="list-none space-y-2 pl-0">
                <li className="flex items-start">
                  <span className="text-[#59B9C1] mr-2">✓</span>
                  <span className="text-gray-300">Enhanced Engagement</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#59B9C1] mr-2">✓</span>
                  <span className="text-gray-300">Improved Loyalty</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#59B9C1] mr-2">✓</span>
                  <span className="text-gray-300">Optimized Resource Allocation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#59B9C1] mr-2">✓</span>
                  <span className="text-gray-300">Increased Effectiveness</span>
                </li>
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="w-full flex items-center justify-center p-0"
          >
            <div className="bg-gray-800/80 rounded-xl p-2 md:p-4 shadow-md w-full aspect-[5/4] flex items-center justify-center border border-gray-700">
              <div className="w-full h-full flex items-center justify-center">
                <DynamicSpiderChart />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default WelcomeStep;
